import type { AudioManager } from '../audio/AudioManager';
import type { Input } from '../core/Input';
import type { QualityLevel } from '../core/Quality';

export type UICallbacks = {
  onBegin: () => void;
  onResume: () => void;
  onQuality: (level: QualityLevel) => void;
};

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export class UI {
  private readonly loading: HTMLElement;
  private readonly loadingBar: HTMLElement;
  private readonly title: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly hints: HTMLElement;
  private readonly prompt: HTMLElement;
  private readonly haiku: HTMLElement;
  private readonly counter: HTMLElement;
  private readonly toast: HTMLElement;
  private readonly edge: HTMLElement;
  private readonly pause: HTMLElement;
  private readonly touch: HTMLElement;
  private readonly fade: HTMLElement;
  private hintsTimer = 0;
  private counterTimer = 0;
  private toastTimer = 0;
  private haikuTimers: number[] = [];
  private hudHidden = false;
  private promptText = '';
  private qualityButtons: HTMLButtonElement[] = [];

  constructor(
    private readonly root: HTMLElement,
    private readonly input: Input,
    private readonly audio: AudioManager,
    private readonly callbacks: UICallbacks,
    private readonly touchMode: boolean,
    quality: QualityLevel,
  ) {
    this.fade = el('div', 'ui-fade');
    this.loading = el('section', 'ui-loading');
    this.loading.append(el('div', 'ui-kanji', '風の道'), el('div', 'ui-loading-label', 'Gathering the wind'));
    const bar = el('div', 'ui-bar');
    this.loadingBar = el('div', 'ui-bar-fill');
    bar.append(this.loadingBar);
    this.loading.append(bar);

    this.title = el('section', 'ui-title hidden');
    const titleBlock = el('div', 'ui-title-block');
    titleBlock.append(el('div', 'ui-kanji ui-kanji-large', '風の道'), el('h1', 'ui-title-name', 'KAZE'), el('div', 'ui-title-sub', 'the way of the wind'));
    const begin = el('button', 'ui-begin', touchMode ? 'Tap to begin' : 'Click to begin');
    begin.type = 'button';
    begin.addEventListener('click', () => {
      this.audio.play('uiConfirm', { group: 'ui', volume: 0.5 });
      this.callbacks.onBegin();
    });
    const credit = el('div', 'ui-credit', 'Headphones recommended · Sound crafted with ElevenLabs');
    this.title.append(titleBlock, begin, credit);

    this.hud = el('section', 'ui-hud hidden');
    this.hints = el('div', 'ui-hints');
    this.hints.innerHTML = touchMode
      ? '<span>DRAG move</span><span>SWIPE look</span><span>刀 sword</span><span>風 wind</span>'
      : '<span><b>W A S D</b> move</span><span><b>MOUSE</b> look</span><span><b>SHIFT</b> sprint</span><span><b>SPACE</b> jump</span><span><b>C</b> slide</span><span><b>1</b> sword</span><span><b>Q</b> wind</span>';
    this.prompt = el('div', 'ui-prompt');
    this.haiku = el('div', 'ui-haiku');
    this.counter = el('div', 'ui-counter');
    this.toast = el('div', 'ui-toast');
    this.edge = el('div', 'ui-edge', 'The wind turns you back toward the fields');
    this.hud.append(this.hints, this.prompt, this.haiku, this.counter, this.toast, this.edge);

    this.pause = el('section', 'ui-pause hidden');
    this.pause.append(this.buildPausePanel(quality));

    this.touch = el('section', 'ui-touch hidden');
    if (touchMode) this.buildTouch();

    root.append(this.fade, this.loading, this.title, this.hud, this.touch, this.pause);
  }

  setLoading(progress: number): void {
    this.loadingBar.style.transform = `scaleX(${Math.max(0.02, Math.min(1, progress))})`;
  }

  showTitle(): void {
    this.loading.classList.add('hidden');
    this.title.classList.remove('hidden');
  }

  startPlaying(): void {
    this.title.classList.add('hidden');
    this.hud.classList.remove('hidden');
    if (this.touchMode) this.touch.classList.remove('hidden');
    this.hints.classList.add('visible');
    this.hintsTimer = 16;
  }

  setPaused(paused: boolean): void {
    this.pause.classList.toggle('hidden', !paused);
    if (paused) this.hints.classList.add('visible');
    else this.hintsTimer = Math.max(this.hintsTimer, 5);
  }

  get paused(): boolean {
    return !this.pause.classList.contains('hidden');
  }

  toggleHud(): void {
    this.hudHidden = !this.hudHidden;
    this.hud.classList.toggle('ui-hud-off', this.hudHidden);
    this.touch.classList.toggle('ui-hud-off', this.hudHidden && !this.touchMode);
  }

  setPrompt(text: string | null): void {
    const next = text ?? '';
    if (next === this.promptText) return;
    this.promptText = next;
    if (next) this.prompt.innerHTML = next;
    this.prompt.classList.toggle('visible', Boolean(next));
    if (this.touchMode) this.touch.querySelector('.touch-pray')?.classList.toggle('visible', Boolean(next));
  }

  showHaiku(lines: string[]): void {
    for (const t of this.haikuTimers) window.clearTimeout(t);
    this.haikuTimers = [];
    this.haiku.innerHTML = '';
    this.haiku.classList.add('visible');
    lines.forEach((line, i) => {
      const span = el('p', 'ui-haiku-line', line);
      this.haiku.append(span);
      this.haikuTimers.push(window.setTimeout(() => span.classList.add('in'), 500 + i * 1300));
    });
    this.haikuTimers.push(window.setTimeout(() => this.haiku.classList.remove('visible'), 11000));
  }

  showCounter(lit: number, total: number, seconds = 6): void {
    this.counter.innerHTML = `<span class="ui-counter-glyph">灯</span><span class="ui-counter-num">${lit}<i>/</i>${total}</span><span class="ui-counter-label">lanterns lit</span>`;
    this.counter.classList.add('visible');
    this.counterTimer = seconds;
  }

  showToast(text: string, seconds = 4.5): void {
    this.toast.textContent = text;
    this.toast.classList.add('visible');
    this.toastTimer = seconds;
  }

  setEdgeHint(on: boolean): void {
    this.edge.classList.toggle('visible', on);
  }

  fadeTo(opacity: number, seconds: number): void {
    this.fade.style.transitionDuration = `${seconds}s`;
    this.fade.style.opacity = String(opacity);
  }

  update(dt: number, moving: boolean): void {
    if (this.hintsTimer > 0) {
      this.hintsTimer -= dt * (moving ? 1 : 0.35);
      if (this.hintsTimer <= 0 && !this.paused) this.hints.classList.remove('visible');
    }
    if (this.counterTimer > 0) {
      this.counterTimer -= dt;
      if (this.counterTimer <= 0) this.counter.classList.remove('visible');
    }
    if (this.toastTimer > 0) {
      this.toastTimer -= dt;
      if (this.toastTimer <= 0) this.toast.classList.remove('visible');
    }
  }

  setQuality(level: QualityLevel): void {
    for (const b of this.qualityButtons) b.classList.toggle('active', b.dataset.level === level);
  }

  private buildPausePanel(quality: QualityLevel): HTMLElement {
    const panel = el('div', 'ui-panel');
    panel.append(el('div', 'ui-panel-kanji', '休'), el('h2', 'ui-panel-title', 'Resting'));
    const resume = el('button', 'ui-button primary', 'Continue');
    resume.type = 'button';
    resume.addEventListener('click', () => {
      this.audio.play('uiConfirm', { group: 'ui', volume: 0.45 });
      this.callbacks.onResume();
    });
    panel.append(resume);

    const slider = (label: string, value: number, onInput: (v: number) => void) => {
      const row = el('label', 'ui-row');
      row.append(el('span', 'ui-row-label', label));
      const input = el('input', 'ui-slider');
      input.type = 'range';
      input.min = '0';
      input.max = '1';
      input.step = '0.01';
      input.value = String(value);
      input.addEventListener('input', () => onInput(Number(input.value)));
      row.append(input);
      return row;
    };
    const s = this.audio.settings;
    panel.append(
      slider('Master', s.master, (v) => {
        s.master = v;
        this.audio.applySettings();
      }),
      slider('Wind & ambience', s.ambience, (v) => {
        s.ambience = v;
        this.audio.applySettings();
      }),
      slider('Effects', s.sfx, (v) => {
        s.sfx = v;
        this.audio.applySettings();
      }),
      slider('Look sensitivity', this.input.sensitivity / 2, (v) => {
        this.input.sensitivity = Math.max(0.1, v * 2);
      }),
    );

    const qRow = el('div', 'ui-row');
    qRow.append(el('span', 'ui-row-label', 'Visual quality'));
    const seg = el('div', 'ui-segment');
    (['low', 'medium', 'high'] as QualityLevel[]).forEach((level) => {
      const b = el('button', `ui-seg${level === quality ? ' active' : ''}`, level[0].toUpperCase() + level.slice(1));
      b.type = 'button';
      b.dataset.level = level;
      b.addEventListener('click', () => {
        this.audio.play('uiHover', { group: 'ui', volume: 0.5 });
        this.setQuality(level);
        this.callbacks.onQuality(level);
      });
      this.qualityButtons.push(b);
      seg.append(b);
    });
    qRow.append(seg);
    panel.append(qRow);

    const invert = el('label', 'ui-row ui-check');
    const box = el('input');
    box.type = 'checkbox';
    box.checked = this.input.invertY;
    box.addEventListener('change', () => (this.input.invertY = box.checked));
    invert.append(el('span', 'ui-row-label', 'Invert look'), box);
    panel.append(invert);

    const keys = el('div', 'ui-keys');
    keys.innerHTML =
      '<span><b>1</b> draw or sheathe</span><span><b>CLICK</b> strike</span><span><b>SPACE ×2</b> flip</span><span><b>C</b> crouch · slide</span>' +
      '<span><b>Q</b> guiding wind</span><span><b>E</b> pay respects</span><span><b>CAPS</b> walk</span><span><b>H</b> hide interface</span>';
    panel.append(keys);
    return panel;
  }

  private buildTouch(): void {
    const stickZone = el('div', 'touch-stick-zone');
    const stick = el('div', 'touch-stick');
    const knob = el('div', 'touch-knob');
    stick.append(knob);
    stickZone.append(stick);
    const lookZone = el('div', 'touch-look-zone');
    const buttons = el('div', 'touch-buttons');
    const mk = (cls: string, label: string, action: () => void) => {
      const b = el('button', `touch-btn ${cls}`, label);
      b.type = 'button';
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        action();
      });
      return b;
    };
    const sprint = mk('touch-sprint', '疾', () => {
      const on = !this.input.touchSprinting;
      this.input.setTouchSprint(on);
      sprint.classList.toggle('active', on);
    });
    // 刀 draws with a cut, then keeps striking; 伏 is held to crouch, or to slide out of a run.
    const crouch = mk('touch-crouch', '伏', () => {
      this.input.setTouchCrouch(true);
      crouch.classList.add('active');
    });
    const release = () => {
      this.input.setTouchCrouch(false);
      crouch.classList.remove('active');
    };
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave'] as const) crouch.addEventListener(ev, release);
    buttons.append(
      crouch,
      mk('touch-pray', '祈', () => this.input.press('interact')),
      mk('touch-jump', '跳', () => this.input.press('jump')),
      mk('touch-wind', '風', () => this.input.press('wind')),
      sprint,
      mk('touch-sword', '刀', () => this.input.press('slash')),
    );
    const pauseBtn = mk('touch-pause', '休', () => this.input.press('pause'));
    this.touch.append(lookZone, stickZone, buttons, pauseBtn);

    let stickId: number | null = null;
    let cx = 0;
    let cy = 0;
    stickZone.addEventListener('pointerdown', (e) => {
      stickId = e.pointerId;
      cx = e.clientX;
      cy = e.clientY;
      stick.style.left = `${cx}px`;
      stick.style.top = `${cy}px`;
      stick.classList.add('active');
      stickZone.setPointerCapture(e.pointerId);
    });
    const endStick = (e: PointerEvent) => {
      if (e.pointerId !== stickId) return;
      stickId = null;
      stick.classList.remove('active');
      knob.style.transform = 'translate(-50%, -50%)';
      this.input.setTouchMove(0, 0);
    };
    stickZone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== stickId) return;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const max = 52;
      const len = Math.hypot(dx, dy);
      const k = len > max ? max / len : 1;
      knob.style.transform = `translate(calc(-50% + ${dx * k}px), calc(-50% + ${dy * k}px))`;
      this.input.setTouchMove((dx * k) / max, (-dy * k) / max);
    });
    stickZone.addEventListener('pointerup', endStick);
    stickZone.addEventListener('pointercancel', endStick);
    stickZone.addEventListener('lostpointercapture', endStick);

    let lookId: number | null = null;
    let lx = 0;
    let ly = 0;
    lookZone.addEventListener('pointerdown', (e) => {
      lookId = e.pointerId;
      lx = e.clientX;
      ly = e.clientY;
      lookZone.setPointerCapture(e.pointerId);
    });
    lookZone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== lookId) return;
      this.input.addTouchLook(e.clientX - lx, e.clientY - ly);
      lx = e.clientX;
      ly = e.clientY;
    });
    const endLook = (e: PointerEvent) => {
      if (e.pointerId === lookId) lookId = null;
    };
    lookZone.addEventListener('pointerup', endLook);
    lookZone.addEventListener('pointercancel', endLook);
  }

  get element(): HTMLElement {
    return this.root;
  }
}
